/**
 * Mutation layer for Launcher V2.
 *
 * ── STATUS ───────────────────────────────────────────────────────────────────
 * Segmentation + Playbook identity CRUD (PR B / U5), the waterfall rung
 * reorder (PR B / U4), `savePlaybook`/`saveAndApplyToCampaigns`/
 * `saveSegmentationConditions`/`createPlay` (PR C / U3C), and — as of PR C /
 * U4B — the whole Sequence surface (`createSequence`, `renameSequence`,
 * `duplicateSequence`, `deleteSequence`, `saveSequenceSteps`,
 * `setSequenceFlow`) are REAL: those functions take the injected `apiFetch`
 * as their first argument (same contract as `api-client.ts`'s read wrappers,
 * supplied by `useApi()` at the call site) and delegate to the typed `/v2/*`
 * client wrappers, which in turn hit endpoints that route every write through
 * `versioning.ts`'s choke points.
 *
 * `sendEnrollments` is REAL as of PR D / U5b: it wraps
 * {@link sendPlayRequest} (`api-client.ts`), which POSTs the routed member
 * ids to the U5a backend's `/plays/:id/send` and parses the two-phase
 * `{ sendId, dispatchId, queued, startFailed }` receipt.
 *
 * Payload types: plain string ids/fields the UI owns are typed directly;
 * anything whose shape is A1-schema-owned (a segmentation/sequence/playbook
 * row, a play) is typed `unknown` — same no-invented-schema rule as
 * `api-client.ts` (R9 / `terra/no-parallel-schema-and-type`).
 */

import {
  archiveIdentityRequest,
  createPlaybookRequest,
  archivePlayRequest,
  clonePlayRequest,
  forkIntoPlayRequest,
  updatePlaysRequest,
  wipePlayDispatchLogRequest,
  resetPlayRequest,
  createPlayRequest,
  createSegmentationRequest,
  createSequenceRequest,
  duplicateIdentityRequest,
  generateSequenceCopyRequest,
  renameIdentityRequest,
  renamePlayRequest,
  reorderPlaybookRungsRequest,
  saveSegmentationConditionsRequest,
  savePlaybookRungsRequest,
  savePlayLadderRequest,
  savePlaySequenceCopyRequest,
  saveSequenceStepsRequest,
  sendPlayRequest,
  setSequenceFlowRequest,
  setSequenceInflectionFieldsRequest,
  setSequenceSenderRequest,
  streamSequenceCopyGeneration as streamSequenceCopyGenerationRequest,
  type OwningPlayOptions,
  type PlaybooksGenerateProgressDoneEvent,
  type SequenceSettingsWriteOptions,
  type StreamSequenceCopyGenerationOptions,
} from './api-client';
import type { AssembledDraftRung } from './playbook-draft';
import type {
  PlaybooksSavePlayLadderResponse,
  PlaybooksClonePlayRequest,
  PlaybooksForkIntoPlayRequest,
  PlaybooksSavePlaySequenceCopyRequest,
  PlaybooksUpdatePlaysRequest,
  PlaybooksUpdatePlaysResponse,
  PlaybooksForkIntoPlayResponse,
  LauncherChannel,
  PlaybooksArchiveIdentityResponse,
  PlaybooksCreateIdentityResponse,
  PlaybooksArchivePlayResponse,
  PlaybooksWipePlayDispatchLogResponse,
  PlaybooksCreatePlayResponse,
  PlaybooksPlaySummary,
  PlaybooksGenerateSequenceCopyRequest,
  PlaybooksGenerateSequenceCopyResponse,
  PlaybooksGenerationInputs,
  PlaybooksRenameIdentityResponse,
  PlaybooksReorderRungsResponse,
  PlaybooksSavePlaybookRungsResponse,
  PlaybooksSaveSegmentationConditionsResponse,
  PlaybooksSavePlaySequenceCopyResponse,
  PlaybooksSaveSequenceStepsResponse,
  PlaybooksSendPlayResponse,
  PlaybooksSetSequenceFlowResponse,
  PlaybooksSetSequenceInflectionFieldsRequest,
  PlaybooksSetSequenceInflectionFieldsResponse,
  PlaybooksSetSequenceSenderResponse,
  SegmentationConditions,
  SequenceSteps,
} from './schemas';

/** The injected throwing `apiFetch` (`_shared/hooks/useApi.ts`), never imported here. */
type ApiFetch = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

// ── Segmentations — REAL as of PR B / U5 ────────────────────────────────────

/**
 * A brand-new Segmentation is created with an EMPTY condition set (legal per
 * `SegmentationConditionsSchema`) and no mapping — the "what does it match"
 * editing pass is a separate `createVersion` the conditions editor will make.
 *
 * The A2 stub took a leading `campaignSfdcId` it never needed (Segmentation
 * identities are not campaign-scoped) and had zero call sites; it is dropped
 * here rather than threaded into a backend that has nowhere to put it.
 */
export function createSegmentation(
  apiFetch: ApiFetch,
  name: string,
  /** Plan 2026-09-25-002, Decision 13 — `owningPlayId` creates the play's own segmentation. */
  opts?: OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  return createSegmentationRequest(apiFetch, name, opts);
}

/** Rename an identity row. Produces no version — the head version number is unchanged. */
export function renameSegmentation(
  apiFetch: ApiFetch,
  id: string,
  name: string,
): Promise<PlaybooksRenameIdentityResponse> {
  return renameIdentityRequest(apiFetch, 'segmentations', id, { name });
}

/**
 * A lineage-clean copy of the source's head content, named `"<name> (copy)"`.
 *
 * Playbooks QA round 4, U6 (S2) / Decision 13 — `opts` is the FORK form: the
 * usage sidebar's "Save a new version" for a shared segmentation passes the
 * rep's draft `conditions` (so the fork's first version carries them, one
 * write) plus `expectedSourceVersionId` (so a source head that moved since the
 * draft was edited answers 409 `version_conflict` instead of minting a fork
 * pairing this draft with a changed source).
 *
 * Omitting `opts` entirely is the plain Config "Duplicate" kebab, whose wire
 * body stays byte-identical to before this unit (no body at all).
 */
export function duplicateSegmentation(
  apiFetch: ApiFetch,
  id: string,
  opts?: { conditions?: SegmentationConditions; expectedSourceVersionId?: string } & OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  return duplicateIdentityRequest(apiFetch, 'segmentations', id, opts);
}

/** Soft delete (`archived_at`) — plays that already reference a version keep resolving it. */
export function deleteSegmentation(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksArchiveIdentityResponse> {
  return archiveIdentityRequest(apiFetch, 'segmentations', id);
}

/**
 * Edits a segmentation's audience conditions — REAL as of PR C / U3C. A
 * LIBRARY-OBJECT edit (writes a new segmentation version immediately), not a
 * playbook-draft edit: the Qualify step's kebab "Edit conditions" calls this
 * directly, the same way every other identity edit in this file is immediate.
 */
export function saveSegmentationConditions(
  apiFetch: ApiFetch,
  id: string,
  conditions: SegmentationConditions,
  expectedVersionId: string,
  /**
   * Playbooks QA round 4, U6 (S2) — the place count the rep approved, sent by
   * the gate in `SegmentationConditionsEditor#handleSave` (sole-use and
   * "Update all" alike). Omitted by nothing today; optional so the arity
   * change is not a breaking one for any caller added later that has no usage
   * fetch of its own.
   */
  confirmedPlaceCount?: number,
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query
   * param; TIGHTENED to a union by plan 2026-09-29-001 U1 (R3) — `group` is
   * required whenever `playId` is given.
   */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksSaveSegmentationConditionsResponse> {
  return saveSegmentationConditionsRequest(
    apiFetch,
    id,
    conditions,
    expectedVersionId,
    confirmedPlaceCount,
    opts,
  );
}

// ── Sequences — CRUD + step persistence + flow lock — REAL as of PR C / U4B ──

/**
 * A brand-new Sequence starts with an EMPTY step array (legal per
 * `SequenceStepsSchema`'s own "a sequence identity can exist before any copy
 * is written" doc) and an unbound `flowId` — binding one is
 * {@link setSequenceFlow}'s exclusive job. `channel` is required with no
 * default: it is identity-INVARIANT (Key Decision 5), chosen once here and
 * unreachable from every other route.
 */
export function createSequence(
  apiFetch: ApiFetch,
  name: string,
  channel: LauncherChannel,
  /** U9 (SN1) — the sequence's locked sender, or `null`/omitted for "Not set". */
  sender?: string | null,
  /** Plan 2026-09-25-002, Decision 13 — `owningPlayId` creates the play's own sequence. */
  opts?: OwningPlayOptions,
): Promise<PlaybooksCreateIdentityResponse> {
  return createSequenceRequest(apiFetch, name, channel, sender, opts);
}

/**
 * Playbooks QA round 4, U9 (SN1) / Decision 3 — sets (or clears, with `null`)
 * the sequence's own `default_sender`. Identity-row write, no new version.
 *
 * Copy-on-map only (Decision 11) for the STORED `mapping.sender` copy: a rung
 * mapped after this edit copies the new value at map time. But the copy is a
 * loading-state fallback only — the sequence's CURRENT `default_sender` is
 * what every surface actually reads (`effectiveSender` in
 * `sender-choice.ts`) and what `planResolveForSend` locks into the send row
 * at the send instant, never the rung's stale copied `mapping.sender`. See
 * commit `653f45770`.
 */
export function setSequenceSender(
  apiFetch: ApiFetch,
  sequenceId: string,
  sender: string | null,
  /** Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query param; plan 2026-09-28-008 U2 — `group` on an owned in-play write. */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksSetSequenceSenderResponse> {
  return setSequenceSenderRequest(apiFetch, sequenceId, sender, opts);
}

/**
 * playbooks-inflection-wiring (2026-09-17) — sets (or clears) the sequence's
 * `default_cc` / `default_reply_to`. Same identity-row-write shape as
 * {@link setSequenceSender}; the server refuses this on any channel but
 * `inflection`.
 */
export function setSequenceInflectionFields(
  apiFetch: ApiFetch,
  sequenceId: string,
  fields: PlaybooksSetSequenceInflectionFieldsRequest,
  /** Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query param; plan 2026-09-28-008 U2 — `group` on an owned in-play write. */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksSetSequenceInflectionFieldsResponse> {
  return setSequenceInflectionFieldsRequest(apiFetch, sequenceId, fields, opts);
}

/** Rename an identity row. Produces no version — the head version number is unchanged. */
export function renameSequence(
  apiFetch: ApiFetch,
  id: string,
  name: string,
  /** Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query param; plan 2026-09-28-008 U2 — `group` on an owned in-play write. */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksRenameIdentityResponse> {
  return renameIdentityRequest(apiFetch, 'sequences', id, { name }, opts);
}

/**
 * A lineage-clean copy of the source's head content, named `"<name> (copy)"`.
 * Per U4A's design note, the copy keeps the source's `channel` AND its bound
 * `flowId` (if any) — duplicating a locked Sequence produces another Sequence
 * whose lock starts already satisfied, never a re-pick prompt.
 *
 * `steps`, when passed, seeds the new identity's v1 with those steps instead
 * of the source's current head steps (round-2-followup plan, Unit B2,
 * decision 3) — the atomic "duplicate with an edited copy" path
 * `duplicate-sequence-copy.ts#duplicateSequenceWithEditedSteps` uses.
 *
 * `generationInputs` (U17, F6), when passed, carries the "Generate with AI"
 * provenance onto that same new identity — the "Save a new version"
 * usage-sidebar decision's equivalent of "Update all"'s own
 * `saveSequenceSteps(..., generationInputs)` forwarding.
 *
 * `expectedSourceVersionId` (U19, M1), when passed, is the SOURCE head those
 * `steps` were edited/generated against — the server refuses with 409
 * `version_conflict` if the source head has moved since, the same guard
 * "Update all" gets from its own `expectedVersionId`. The plain Config
 * "Duplicate" kebab omits it: it copies whatever the live head is by
 * definition, so it has nothing stale to guard.
 *
 * All three trailing params are optional so every existing 2-/3-/4-arg caller
 * (e.g. `PlaybooksConfigPage.tsx`'s plain "Duplicate" kebab action) keeps
 * compiling AND sends a byte-identical wire body — the body is built here from
 * only the fields actually passed, never `{ generationInputs: undefined }`.
 */
export function duplicateSequence(
  apiFetch: ApiFetch,
  id: string,
  steps?: SequenceSteps,
  generationInputs?: PlaybooksGenerationInputs,
  expectedSourceVersionId?: string,
): Promise<PlaybooksCreateIdentityResponse> {
  const body =
    steps !== undefined || generationInputs !== undefined || expectedSourceVersionId !== undefined
      ? {
          ...(steps !== undefined ? { steps } : {}),
          ...(generationInputs !== undefined ? { generationInputs } : {}),
          ...(expectedSourceVersionId !== undefined ? { expectedSourceVersionId } : {}),
        }
      : undefined;
  return duplicateIdentityRequest(apiFetch, 'sequences', id, body);
}

/**
 * Playbooks QA round 6, U5 (item 3) — the sender popover's "Save as a new
 * sequence" fork: a lineage-clean copy of the source's CURRENT head content
 * (steps, channel, flow binding) with `sender` overriding the source's own
 * `default_sender` for this one new identity. A separate function rather
 * than a 6th positional param on {@link duplicateSequence} — that function's
 * own `api-client.test.ts` pins its existing 2-/3-/4-arg wire bodies, and
 * this caller always sends `sender` (even `null`), which those pins must
 * not have to account for.
 */
export function duplicateSequenceWithSender(
  apiFetch: ApiFetch,
  id: string,
  sender: string | null,
): Promise<PlaybooksCreateIdentityResponse> {
  return duplicateIdentityRequest(apiFetch, 'sequences', id, { sender });
}

/** Soft delete (`archived_at`) — plays that already reference a version keep resolving it. */
export function deleteSequence(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksArchiveIdentityResponse> {
  return archiveIdentityRequest(apiFetch, 'sequences', id);
}

/**
 * Saves a sequence's WHOLE copy as a new version (`StepEditorShell`'s Save
 * button, via `SequenceEditorShell`). `steps` is the complete array with the
 * edited step already merged in — a sequence version is a self-contained
 * snapshot (Key Decision 12), so there is no per-step write. `expectedVersionId`
 * is the sequence version id the caller's steps were actually read from; the
 * server refuses with `version_conflict` (409) if the head moved since,
 * surfacing the same reload-and-retry UX `savePlaybook`'s 409 path already
 * ships.
 *
 * `confirmedMappingCount`, when passed, is the shared-save acknowledgment
 * (round-2-followup plan, Unit B2, decision 1) — omitted, the server assumes
 * `1` (the no-dialog sole-use save path), matching today's behavior for every
 * existing caller.
 *
 * `generationInputs`, when passed (U7a), carries the "Generate with AI"
 * provenance for this save — additive, omitted by every caller predating AI
 * Apply routing through this ordinary save.
 *
 * `confirmedUnsentPlayCount`, when passed (round-4 QA fixes, U5/S1), is the
 * unsent-play acknowledgment forwarded verbatim to
 * {@link saveSequenceStepsRequest} — see that function's own doc.
 */
export function saveSequenceSteps(
  apiFetch: ApiFetch,
  sequenceId: string,
  steps: SequenceSteps,
  expectedVersionId: string,
  confirmedMappingCount?: number,
  generationInputs?: PlaybooksGenerationInputs,
  confirmedUnsentPlayCount?: number,
  /**
   * Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query
   * param; TIGHTENED to a union by plan 2026-09-29-001 U1 (R3) — `group` is
   * required whenever `playId` is given.
   */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksSaveSequenceStepsResponse> {
  return saveSequenceStepsRequest(
    apiFetch,
    sequenceId,
    steps,
    expectedVersionId,
    confirmedMappingCount,
    generationInputs,
    confirmedUnsentPlayCount,
    opts,
  );
}

/**
 * Saves the play's own whole-sequence copy (U4, `SequencesStep.tsx`'s
 * `saveWithGate` in play context). Thin pass-through to
 * {@link savePlaySequenceCopyRequest} — same house style as
 * {@link saveSequenceSteps} — because the play flow calls this directly with
 * no additional logic of its own; the usage-gate skip and the
 * template-vs-copy `expectedVersionId` choice are the CALLER's job
 * (`saveWithGate`, U4 approach), not this wrapper's.
 */
export function savePlaySequenceCopy(
  apiFetch: ApiFetch,
  playId: string,
  sequenceId: string,
  steps: SequenceSteps,
  /** The head of the play's own sequence the caller rendered (plan 2026-09-25-002, Decision 7). */
  expectedVersionId: string,
  // No `?` — TS forbids a required parameter (the now-mandatory `options`
  // below) from following an optional one. Every caller must pass this slot
  // explicitly (`undefined` is fine and the wire body still omits the key).
  generationInputs: PlaybooksGenerationInputs | undefined,
  /**
   * The group the save is for (REQUIRED — plan 2026-09-29-001 U1, rulebook
   * R3/R17: the session mount 400s `group_required` on a body with no
   * `rungId`), the copy mode (plan 2026-09-28-006 U1, R3), and an owned
   * update's confirmed groups (plan 2026-09-28-007 U3, R8).
   */
  options: Pick<PlaybooksSavePlaySequenceCopyRequest, 'mode' | 'confirmedRungIds'> & { rungId: string },
): Promise<PlaybooksSavePlaySequenceCopyResponse> {
  return savePlaySequenceCopyRequest(apiFetch, playId, sequenceId, steps, expectedVersionId, generationInputs, options);
}

/**
 * True when `error` is the server's `shared_mapping_conflict` (409) — the
 * save gate's `confirmedMappingCount` was stale by the time the write
 * actually landed (round-3-QA-fixes plan, decision 3/9). Never shown raw:
 * the gate that hits this refetches usage and re-opens itself with the
 * fresh count and list instead of surfacing this code or its `detail` text.
 * Same shape as `playbook-draft-store.ts`'s `isVersionConflictError` — the
 * wire `code` reaching the client is round-2-followup-codex-fixes decision 2
 * (`handleError` adds `code: err.code`).
 */
export function isSharedMappingConflictError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === 'shared_mapping_conflict'
  );
}

/**
 * Binds (or REBINDS, Batch 6 D4) a Sequence to a flow — a real Gong flow for
 * `gong_engage` (still catalog-validated server-side); free-text on every
 * other channel (playbooks-inflection-wiring, 2026-09-17 — see
 * `SequenceFlowIdPopover`). There is still no unset anywhere in the client.
 * Calling this with the flow already bound is idempotent (200, no new
 * write); calling it with a DIFFERENT flow than the one already bound now
 * REBINDS it (200, the new binding) instead of refusing — the one-way lock
 * is repealed.
 * Callers that rebind an already-bound Sequence must pair this with their own
 * whole-snapshot step re-import (`saveSequenceSteps`) in the same action, per
 * `setSequenceFlowId`'s doc — this function does not do that on the caller's
 * behalf. A genuine concurrent race can still throw a 409 `version_conflict`
 * `FetchErrorLike` telling the caller to reload and pick again.
 */
export function setSequenceFlow(
  apiFetch: ApiFetch,
  sequenceId: string,
  flowId: string,
  /** Plan 2026-09-25-002, U7 (Decision 21) — the ownership guard's query param; plan 2026-09-28-008 U2 — `group` on an owned in-play write. */
  opts?: SequenceSettingsWriteOptions,
): Promise<PlaybooksSetSequenceFlowResponse> {
  return setSequenceFlowRequest(apiFetch, sequenceId, flowId, opts);
}

/**
 * "Generate with AI" for one sequence — REAL as of U12A / R16
 * (`POST /v2/sequences/:id/generate-copy`). This is the entry point U12B's
 * Generate button calls inside its `useMutation`.
 *
 * WRITES NOTHING, which is the whole point of its shape: it RESOLVES with a
 * generated `SequenceSteps` draft plus the `headVersionId` that draft was
 * generated against, and the rep's "Accept" is then an ordinary
 * {@link saveSequenceSteps} call passing that same id as `expectedVersionId`.
 * Generated copy therefore travels the one version write path with the one
 * lost-update guard — a generation accepted after the head moved under the rep
 * fails with the same 409 `version_conflict` a hand-typed save would.
 *
 * `input` mirrors the request schema: only `campaignSfdcId` is required, and
 * each omission resolves server-side to a real source, never a guessed default
 * — the campaign row's own `description`, no source material, no status
 * filter, and the sequence's saved head steps. `baseSteps` is an unused
 * optional override point — corrected comment (PR C QA follow-ups): no shipped
 * caller passes it (this file's own `generateSequenceCopy` call site,
 * `SequencesStep.tsx`'s `handleGenerate`, omits it), so every real request
 * rewrites from the saved head. The prototype's rule this field was modeled on
 * — a Gong-backed sequence rewrites from the flow's default steps instead — is
 * not wired up anywhere and is out of scope for this unit.
 *
 * Errors arrive as thrown `FetchErrorLike`s, by cause: 404 unknown sequence,
 * 409 archived sequence, 400 unknown campaign, 502 model/transport failure or
 * unusable model output (`userMessage` carries the server's `detail`, which is
 * written to be shown to a rep). A caller that wants to present the failure
 * itself — e.g. an inline error beside the Generate button, leaving the current
 * draft untouched — passes `meta: { suppressErrorToast: true }` on its
 * `useMutation` and handles `onError` locally.
 */
export function generateSequenceCopy(
  apiFetch: ApiFetch,
  sequenceId: string,
  input: PlaybooksGenerateSequenceCopyRequest,
): Promise<PlaybooksGenerateSequenceCopyResponse> {
  return generateSequenceCopyRequest(apiFetch, sequenceId, input);
}

/**
 * Streaming counterpart of {@link generateSequenceCopy} (U4b) — the
 * `GenerateWithAIDrawer` (U7) calls this instead so each step card can flip
 * into review as its own `step` frame arrives, rather than waiting for the
 * whole generation to finish. Same thin pass-through shape as every other
 * wrapper in this file: `api-client.ts` owns the actual transport/parsing.
 */
export function generateSequenceCopyStreaming(
  apiFetch: ApiFetch,
  sequenceId: string,
  input: PlaybooksGenerateSequenceCopyRequest,
  options?: StreamSequenceCopyGenerationOptions,
): Promise<PlaybooksGenerateProgressDoneEvent> {
  return streamSequenceCopyGenerationRequest(apiFetch, sequenceId, input, options);
}

// ── Playbooks — CRUD REAL as of PR B / U5; save/apply still ships in PR C ───

/**
 * A brand-new Playbook starts with an EMPTY ladder, including no catch-all.
 * `PlaybookRungsSchema` tolerates that on purpose (it has to accept
 * intermediate editing states) — a pre-existing documented state, not a gap
 * introduced here.
 *
 * `description` is optional (omitted, the server stores `null`) —
 * `CreatePlaySidebar`'s "Create new" leg is the only caller that passes one;
 * the Config page's plain create form still calls this with just a name.
 */
export function createPlaybook(
  apiFetch: ApiFetch,
  name: string,
  description?: string | null,
): Promise<PlaybooksCreateIdentityResponse> {
  return createPlaybookRequest(apiFetch, name, description);
}

export function renamePlaybook(
  apiFetch: ApiFetch,
  id: string,
  name: string,
): Promise<PlaybooksRenameIdentityResponse> {
  return renameIdentityRequest(apiFetch, 'playbooks', id, { name });
}

export function duplicatePlaybook(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksCreateIdentityResponse> {
  return duplicateIdentityRequest(apiFetch, 'playbooks', id);
}

/**
 * Terra-admin-only (R8), enforced by `requireRole('admin', 'acting')` on the
 * route itself — NOT by this function and NOT by the Config page's looser
 * `isLauncherAdmin()` kebab gate. A `launcher_admin` who is not a Terra admin
 * gets a thrown 403 here even if the UI showed them the option.
 */
export function deletePlaybook(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksArchiveIdentityResponse> {
  return archiveIdentityRequest(apiFetch, 'playbooks', id);
}

/**
 * The write-through save every ladder-mutating action posts through
 * (`playbook-draft-store.ts#persistLadder`, 2026-09-02 rip-out, U4) — not a
 * standalone "Save Playbook" button anymore (that button is deleted). Posts
 * the whole current ladder (`playbook-draft.ts#assembleDraftRungs`'s output)
 * plus the draft's `baseVersionId` as `expectedVersionId`; the endpoint mints
 * `crypto.randomUUID()` for any rung that arrived without one (Decision 1) and
 * echoes the persisted ladder back, so the CALLER (`persistLadder`) is
 * responsible for feeding that response into
 * `playbook-draft-store#adoptSavedPlaybookDraft` — this function does not
 * reach into the store itself, the same separation `reorderWaterfall` keeps
 * from `WaterfallCard`.
 */
export function savePlaybook(
  apiFetch: ApiFetch,
  playbookId: string,
  rungs: AssembledDraftRung[],
  expectedVersionId: string,
): Promise<PlaybooksSavePlaybookRungsResponse> {
  return savePlaybookRungsRequest(apiFetch, playbookId, rungs, expectedVersionId);
}

/**
 * Review's "Apply to campaigns" action — REAL as of PR C / U3C, now routed
 * through `persistLadder`'s identity op (2026-09-02 rip-out, U4 — see
 * `ReviewStep.tsx`'s module doc) rather than called directly from a "Save &
 * apply to campaigns" gate button. The SAME endpoint as {@link savePlaybook}
 * with `applyToCampaignSfdcIds` populated (plan Decision: the save and the
 * Plays it creates must agree on which version they were created against) —
 * never a second, separate `POST /v2/plays` round trip for this leg.
 */
export function saveAndApplyToCampaigns(
  apiFetch: ApiFetch,
  playbookId: string,
  rungs: AssembledDraftRung[],
  expectedVersionId: string,
  campaignSfdcIds: string[],
): Promise<PlaybooksSavePlaybookRungsResponse> {
  return savePlaybookRungsRequest(apiFetch, playbookId, rungs, expectedVersionId, campaignSfdcIds);
}

/**
 * Plan 2026-09-25-002, U4 (Decisions 5, 16) — write a PLAY's whole ladder
 * (`POST /plays/:id/ladder`): the play-mode counterpart of
 * {@link savePlaybook}, including reorder. Thin pass-through to
 * {@link savePlayLadderRequest}; the caller adopts the response's rungs
 * (Library ids kept, a Library id the play has a copy of rewritten to it).
 */
export function savePlayLadder(
  apiFetch: ApiFetch,
  playId: string,
  rungs: AssembledDraftRung[],
  expectedVersionId: string,
): Promise<PlaybooksSavePlayLadderResponse> {
  return savePlayLadderRequest(apiFetch, playId, rungs, expectedVersionId);
}

// ── Plays ────────────────────────────────────────────────────────────────────

/**
 * Plan 2026-09-27-006 U3.11 (rung-scoped by plan 2026-09-28-006 U1) — give
 * ONE group (`body.rungId`) its own new copy of the segmentation / sequence it
 * points at (`POST /plays/:id/fork`) before a play-local identity write the
 * ownership middleware would otherwise refuse. Thin pass-through to
 * {@link forkIntoPlayRequest}.
 */
export function forkIntoPlay(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksForkIntoPlayRequest,
): Promise<PlaybooksForkIntoPlayResponse> {
  return forkIntoPlayRequest(apiFetch, playId, body);
}

/**
 * Plan 2026-09-28-006 U1 (wire; endpoint in U2) — option ② of the in-play
 * Save menu, "Update N plays": one change applied to every unsent play still
 * sharing a Library piece, as per-group copies (`POST /plays/:id/update-plays`).
 * Thin pass-through to {@link updatePlaysRequest}.
 */
export function updatePlays(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksUpdatePlaysRequest,
): Promise<PlaybooksUpdatePlaysResponse> {
  return updatePlaysRequest(apiFetch, playId, body);
}

/**
 * Plan 2026-09-27-006 U3.11 — Apply to campaigns: a new draft play carrying
 * this play's edits (`POST /plays/:id/clone`). `body.expectedVersionId` is the
 * play-scoped ladder the rep reviewed (plan 2026-09-27-007 Key Decision 6 — a
 * moved pin is 409 `version_conflict`); omitted `campaignSfdcId` / `name` =
 * the source play's. Thin pass-through to {@link clonePlayRequest}.
 */
export function clonePlay(
  apiFetch: ApiFetch,
  playId: string,
  body: PlaybooksClonePlayRequest,
): Promise<PlaybooksCreatePlayResponse> {
  return clonePlayRequest(apiFetch, playId, body);
}

/**
 * Creates a real, persisted Play against a KNOWN playbook — REAL as of PR C /
 * U3C (`POST /v2/plays`). Called by `CreatePlaySidebar` (`PlaybooksHome`'s
 * "Create Play" button), which resolves `playbookId` either from an
 * existing-Playbook picker or from an inline {@link createPlaybook} call —
 * replacing the old client-only `createDraftPlay` fake, which minted an
 * unpersisted `draft-<uuid>` id and left the wizard permanently unable to
 * load a real Play.
 *
 * QA round 7 (item 10) — the Create Play sidebar no longer collects a play
 * name at create time, so this no longer takes an optional `name` param; the
 * request body is always `{ campaignSfdcId, playbookId }`.
 */
export function createPlay(
  apiFetch: ApiFetch,
  campaignSfdcId: string,
  playbookId: string,
): Promise<PlaybooksCreatePlayResponse> {
  return createPlayRequest(apiFetch, { campaignSfdcId, playbookId });
}

/**
 * U10 (P2) — renames a play, or CLEARS its name with `null` (back to showing
 * the playbook's name). Resolves with the full refreshed summary.
 */
export function renamePlay(
  apiFetch: ApiFetch,
  id: string,
  name: string | null,
): Promise<PlaybooksPlaySummary> {
  return renamePlayRequest(apiFetch, id, name);
}

/**
 * U10 (P1) — SOFT-archives a play (`archived_at`). It leaves this campaign's
 * play list and every usage view; its send records are kept, and nothing it
 * references (segmentation / sequence / playbook) is touched. Any
 * authenticated Playbooks user may call this (Decision 7); a second call
 * answers 409.
 */
export function archivePlay(
  apiFetch: ApiFetch,
  id: string,
): Promise<PlaybooksArchivePlayResponse> {
  return archivePlayRequest(apiFetch, id);
}

/**
 * Settings page admin tool (plan `playbooks-clay-wire-gaps`, U5b) — wipes
 * every `launcher_dispatch_log` row a play ever wrote (real sends AND test
 * sends). `playbooks_play_send` rows (the play's own immutable send history)
 * are untouched. The caller is responsible for invalidating the affected
 * play's `playSendsQueryKey` (`dispatch-inspector-logic.ts`) — this wrapper
 * is a thin pass-through, same as every other function in this file.
 */
export function wipePlayDispatchLog(
  apiFetch: ApiFetch,
  playId: string,
): Promise<PlaybooksWipePlayDispatchLogResponse> {
  return wipePlayDispatchLogRequest(apiFetch, playId);
}

/**
 * Settings page admin tool (plan `playbooks-clay-wire-gaps`, U5b) — resets a
 * `launched` play back to `draft` so it can be re-launched. Send history
 * (`playbooks_play_send`) is untouched; the play simply counts as "unsent"
 * again for `usage.ts#isUnsentPlay`. Resolves with the refreshed summary.
 */
export function resetPlayToDraft(
  apiFetch: ApiFetch,
  playId: string,
): Promise<PlaybooksPlaySummary> {
  return resetPlayRequest(apiFetch, playId);
}

/**
 * Waterfall rung reorder — REAL as of PR B / U4 (R6). Persists the new order as
 * a new MAIN-LINE `PlaybooksPlaybookVersion` through `versioning.ts`'s
 * `createVersion()` choke point; the visible drag in `QualifyStep`'s
 * `WaterfallCard` stays optimistic local state and is reconciled by the
 * playbook refetch this resolves into.
 *
 * SIGNATURE CHANGE from the A2 stub, which took `(campaignSfdcId,
 * orderedRungIds)`: rungs live on a `PlaybooksPlaybookVersion.rungs` array, so a
 * campaign id cannot identify which ladder to reorder — one campaign can host
 * several plays pointing at different playbooks (Decision 14). The ids are the
 * rungs' stable `id`s, in their new priority order, excluding the catch-all.
 *
 * `expectedVersionId` (Codex adversarial-review fix, PR B follow-up): the
 * playbook version id `WaterfallCard`'s ladder was actually rendered from —
 * required so the server can refuse a reorder computed against a head that
 * moved under the caller (`version_conflict` → 409) instead of silently
 * overwriting a concurrent change.
 */
export function reorderWaterfall(
  apiFetch: ApiFetch,
  playbookId: string,
  orderedRungIds: string[],
  expectedVersionId: string,
): Promise<PlaybooksReorderRungsResponse> {
  return reorderPlaybookRungsRequest(apiFetch, playbookId, orderedRungIds, expectedVersionId);
}

/**
 * U6's "Send N enrollments" gate button — REAL as of PR D / U5b.
 *
 * `memberIds` is `ReviewStep`'s `tally.routedMemberIds` — the routed subset
 * the rep reviewed, not the whole roster. The server re-derives the ladder
 * at the send instant and refuses (409 `version_conflict`) if this set
 * disagrees with what it computes, so this is a fail-closed cross-check,
 * not the source of truth for who gets sent to.
 *
 * `isTest` is REQUIRED (U7b) — `ReviewStep`'s Test send / Real send toggle is
 * the only caller and it always has an explicit mode (default `'test'`), so
 * there is no "forgot to pass it" caller to default-guard against here; the
 * wire-level default-free requirement lives on `PlaybooksSendPlayRequestSchema`
 * itself (`schemas.ts`).
 */
export function sendEnrollments(
  apiFetch: ApiFetch,
  playId: string,
  memberIds: string[],
  isTest: boolean,
): Promise<PlaybooksSendPlayResponse> {
  return sendPlayRequest(apiFetch, playId, { memberIds, isTest });
}

/**
 * The Review side panel's one-person TEST (plan 2026-10-15-003 U2) — also "Test selected":
 * `memberId` may be a list of chosen people, sent as ONE test (one dispatch).
 *
 * `routedMemberIds` is the FULL routed roster (`tally.routedMemberIds`), still
 * cross-checked exactly by the server; `memberId` is the person to test, sent
 * as `testMemberIds: [memberId]` — the server narrows to them only after the
 * roster check passes, refuses them by name (409) if they no longer route, and
 * skips the play's one-test-in-flight guard for a chosen-people test. Always
 * `isTest: true`: the schema refuses `testMemberIds` on a real send.
 */
export function sendMemberTest(
  apiFetch: ApiFetch,
  playId: string,
  routedMemberIds: string[],
  memberId: string | readonly string[],
): Promise<PlaybooksSendPlayResponse> {
  const chosen = typeof memberId === 'string' ? [memberId] : [...memberId];
  return sendPlayRequest(apiFetch, playId, { memberIds: routedMemberIds, isTest: true, testMemberIds: chosen });
}
